import { Spin, message, Checkbox, Row, Col } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { useEffect, useState } from 'react';

import { Form, FormItem } from '../../../../components/antd/Form';
import { Input } from '../../../../components/antd/Input';
import { Select } from '../../../../components/antd/Select';
import {
  useCreateCargoTypeMutation,
  useUpdateCargoTypeMutation,
  useGetCargoTypeWithDockingPlacesQuery,
  useGetProductGroupsQuery,
} from '../../../../services/cargoApi';
import { useGetBerthsQuery } from '../../../../services/dockingPlacesApi';
import { CargoType } from '../../../../types';
import { cargoNatureList } from '../../../../utils/lists';
import { isNullOrUndefined } from '../../../../utils/utils';
import {
  RegisterHeaderForm,
  RegisterHeaderFormProps,
} from '../registerHeaderForm';
import { FormItemsWrapper } from './cargoForm';

type CargoTypeFormProps = RegisterHeaderFormProps & {
  selectedCargoType?: CargoType | null;
};

export function CargoTypeForm(props: CargoTypeFormProps) {
  const { selectedCargoType } = props;
  const [cargoNatureSelected, setCargoNatureSelected] = useState(0);

  const [form] = Form.useForm();
  const [
    createCargoType,
    { isSuccess: isSuccessCreate, isError: isErrorCreate },
  ] = useCreateCargoTypeMutation();
  const [
    updateCargoType,
    { isSuccess: isSuccessUpdate, isError: isErrorUpdate },
  ] = useUpdateCargoTypeMutation();

  const isEdit = Boolean(
    selectedCargoType?.name || selectedCargoType?.description
  );

  const { data: cargoTypeWithBerths } = useGetCargoTypeWithDockingPlacesQuery(
    selectedCargoType?.name ?? '',
    { skip: !selectedCargoType?.name }
  );

  // Fetch all product groups for the select (independent of parent pagination)
  const { data: allProductGroupsData } = useGetProductGroupsQuery({
    page_size: 999,
  });
  const allProductGroups = allProductGroupsData?.results || [];

  const {
    data: berthData,
    isLoading: isLoadingBerthData,
    isSuccess: isSuccessLoadingBerth,
  } = useGetBerthsQuery({
    shipType: '',
  });

  function getBerthList() {
    return (berthData?.results || []).reduce((filteredBerths, berth) => {
      if (berth.place_type === 'BERTH') {
        filteredBerths.push({
          label: berth.name,
          value: berth.id,
        });
      }
      return filteredBerths;
    }, []);
  }

  function onChangeDockingPlaces(dockingPlaces: CheckboxValueType[]) {
    form.setFieldsValue({ docking_places: dockingPlaces });
  }

  async function handleFormSubmit(values: any) {
    if (isEdit && selectedCargoType?.name) {
      await updateCargoType({
        name: selectedCargoType.name,
        description: values.description,
        product_group: values.product_group,
        docking_places: values.docking_places,
      });
    } else {
      await createCargoType({
        description: values.description,
        product_group: values.product_group,
        docking_places: values.docking_places,
      } as any);
    }
  }

  useEffect(() => {
    if (isSuccessCreate) {
      message.success('Tipo de mercadoria criado');
      props.onClose(null);
    } else if (isSuccessUpdate) {
      message.success('Tipo de mercadoria atualizado');
      props.onClose(null);
    } else if (isErrorCreate || isErrorUpdate) {
      message.error(
        'Ocorreu um erro ao salvar o tipo de mercadoria, se o erro persistir entre em contato com o suporte'
      );
    }
  }, [isSuccessCreate, isSuccessUpdate, isErrorCreate, isErrorUpdate]);

  useEffect(() => {
    if (selectedCargoType) {
      const natureId = selectedCargoType.product_group?.nature_of_cargo?.id;
      if (natureId) {
        setCargoNatureSelected(natureId);
      }
      form.setFieldsValue({
        description: selectedCargoType.description || selectedCargoType.name,
        product_group: selectedCargoType.product_group?.id,
        cargo_nature: natureId,
      });
    } else {
      setCargoNatureSelected(0);
    }
  }, [selectedCargoType, form]);

  // Load docking places from the with-docking-places endpoint
  useEffect(() => {
    if (cargoTypeWithBerths?.docking_places) {
      const dockingPlaceIds = cargoTypeWithBerths.docking_places.map(
        (dp: any) => dp.id ?? dp.tag
      );
      form.setFieldsValue({ docking_places: dockingPlaceIds });
    }
  }, [cargoTypeWithBerths, form]);

  function onChangeCargoNature(e: any) {
    if (isNullOrUndefined(e)) {
      setCargoNatureSelected(0);
    } else {
      setCargoNatureSelected(e);
    }
    form.setFieldsValue({ product_group: null });
  }

  function onChangeProductGroup(e: any) {
    if (!isNullOrUndefined(e)) {
      const productGroupSelected = allProductGroups.find((p) => p.id === e);
      form.setFieldsValue({
        cargo_nature: productGroupSelected?.nature_of_cargo.id,
      });
      setCargoNatureSelected(productGroupSelected?.nature_of_cargo.id || 0);
    }

    form.setFieldsValue({ cargo_type: null });
  }

  const initialValues = selectedCargoType
    ? {
        description: selectedCargoType.description || selectedCargoType.name,
        product_group: selectedCargoType.product_group?.id,
        cargo_nature: selectedCargoType.product_group?.nature_of_cargo?.id,
      }
    : undefined;

  return (
    <Form
      form={form}
      name="cargo_type"
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={initialValues}
    >
      <RegisterHeaderForm onClose={props.onClose} formName="cargo_type">
        <div className="title">
          {isEdit ? 'EDITAR TIPO DE MERCADORIA' : 'NOVO TIPO DE MERCADORIA'}
        </div>
      </RegisterHeaderForm>
      <FormItemsWrapper>
        <FormItem name="cargo_nature" label="Natureza da carga">
          <Select onChange={(e) => onChangeCargoNature(e)} allowClear>
            {cargoNatureList.map((currentCargoNature) => {
              return (
                <Select.Option
                  key={currentCargoNature.id}
                  value={currentCargoNature.id}
                >
                  {currentCargoNature.name}
                </Select.Option>
              );
            })}
          </Select>
        </FormItem>
        <FormItem
          label="Grupo de produto"
          rules={[{ required: true, message: 'Campo obrigatório' }]}
          name="product_group"
        >
          <Select
            notFoundContent="Selecione a natureza da carga"
            allowClear
            onChange={(e) => onChangeProductGroup(e)}
          >
            {allProductGroups
              .filter((productGroup) =>
                cargoNatureSelected === 0
                  ? productGroup
                  : productGroup.nature_of_cargo.id === cargoNatureSelected
              )
              .map((productGroup) => {
                return (
                  <Select.Option key={productGroup.id} value={productGroup.id}>
                    {productGroup.name}
                  </Select.Option>
                );
              })}
          </Select>
        </FormItem>
        <FormItem
          label="Nome do tipo de mercadoria"
          rules={[{ required: true, message: 'Campo obrigatório' }]}
          name="description"
        >
          <Input />
        </FormItem>
        <Spin
          style={{ marginTop: '8px' }}
          spinning={isLoadingBerthData}
          tip="Carregando..."
        >
          <FormItem
            name="docking_places"
            label="Selecione os berços que aceitam o tipo de mercadoria"
          >
            <Checkbox.Group onChange={onChangeDockingPlaces}>
              <Row>
                {getBerthList().map((berth: any) => (
                  <Col style={{ marginBottom: '4px' }} span={6}>
                    <Checkbox value={berth.value}>{berth.label}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </FormItem>
        </Spin>
      </FormItemsWrapper>
    </Form>
  );
}
